// Define user role and status enums locally to avoid import issues
export enum UserRole {
  ADMIN = "ADMIN",
  USER = "USER",
}

export enum UserStatus {
  ACTIVE = "ACTIVE",
  INACTIVE = "INACTIVE",
  PENDING = "PENDING",
  SUSPENDED = "SUSPENDED",
}

export enum OrgRole {
  OWNER = "OWNER",
  ADMIN = "ADMIN",
  MEMBER = "MEMBER",
  VIEWER = "VIEWER",
}

export enum OrgStatus {
  TRIAL = "TRIAL",
  ACTIVE = "ACTIVE",
  SUSPENDED = "SUSPENDED",
  ARCHIVED = "ARCHIVED",
}

export interface Plan {
  id: string;
  name: string;
  displayName: string;
  maxUsers: number;
  maxDocuments: number;
  maxApiCalls: number;
  features: Record<string, any>;
  monthlyPriceCents: number;
  isActive: boolean;
}

export interface Organization {
  id: string;
  name: string;
  slug: string;
  status: OrgStatus;
  plan: Plan;
  settings?: Record<string, any>;
  customApiKey?: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface OrganizationMember {
  id: string;
  userId: string;
  organizationId: string;
  role: OrgRole;
  joinedAt: string;
  organization?: Organization;
}

/**
 * Enhanced authentication types with improved type safety
 */

export interface User {
  id: string;
  email: string;
  name: string;
  role: UserRole;
  status: UserStatus;
  forcePasswordChange?: boolean;
  firstName?: string;
  lastName?: string;
  accountStatus?: string;
  mfaEnabled?: boolean;
  mfaType?: string | null;
  isActive?: boolean;
  createdAt?: string;
  updatedAt?: string;
  lastLoginAt?: string | null;
  lastPasswordChange?: string | null;
  lastFailedLoginAttempt?: string | null;
  pendingEmail?: string | null;
  isSuperAdmin?: boolean;
  hasOrganization?: boolean;
  organization?: Organization;
  membership?: OrganizationMember;
}

export interface AuthState {
  user: User | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  isInitialized: boolean;
  requiresMfa: boolean;
  mfaEmail: string | null; // Added to track which email is in MFA flow
  mfaSessionExpiry: number | null; // Timestamp when MFA session will expire
  error: string | null;
}

export interface LoginCredentials {
  email: string;
  password: string;
}

export interface MfaCredentials {
  email: string;
  code: string;
}

export interface ResetPasswordCredentials {
  token: string;
  password: string;
  confirmPassword: string;
}

export interface ChangePasswordCredentials {
  currentPassword: string;
  newPassword: string;
  confirmPassword: string;
}

export interface AuthContextType extends AuthState {
  login: (credentials: LoginCredentials) => Promise<void>;
  verifyMfa: (credentials: MfaCredentials) => Promise<void>;
  resendMfaCode: (email: string) => Promise<void>;
  logout: () => Promise<void>;
  resetPasswordRequest: (email: string) => Promise<void>;
  resetPassword: (credentials: ResetPasswordCredentials) => Promise<void>;
  changePassword: (credentials: ChangePasswordCredentials) => Promise<void>;
  clearError: () => void; // Added to allow clearing error state
  refreshUser: () => Promise<void>; // Added to refresh current user data
}
